import { ReactElement, useMemo, useCallback } from 'react'
import {
  Box,
  Card,
  Typography,
  Chip,
  Alert,
  Skeleton,
  Button,
} from '@mui/material'
import {
  DataGridPremium,
  GridRowSelectionModel,
  GridColDef,
} from '@mui/x-data-grid-premium'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'

import {
  getZodValidationError,
  shouldShowZodFieldError,
} from 'src/v3/utilities/validation'
import { dataGridSx } from '../../styles'
import {
  DATAGRID_DEFAULT_PAGE_SIZE,
  DATAGRID_PAGE_SIZE_OPTIONS,
} from '../../constants'
import type { UserRow } from '../utils/user-transforms'
import UserFilters from './user-filters'

interface FormFieldState {
  state: {
    meta: any
  }
}

interface UsersDataGridProps {
  users: UserRow[]
  selectedUsers: GridRowSelectionModel
  onSelectionChange: (newSelection: GridRowSelectionModel) => void
  searchQuery: string
  licenseTypeFilter: string
  availableLicenseTypes: string[]
  onSearchChange: (value: string) => void
  onLicenseTypeChange: (value: string) => void
  onResetFilters: () => void
  isLoading?: boolean
  field: FormFieldState
  editing?: boolean
  title?: string
}

let userColumns: GridColDef[] = [
  {
    field: 'name',
    headerName: 'Name',
    flex: 2,
    minWidth: 200,
  },
  {
    field: 'primaryEmail',
    headerName: 'Email',
    flex: 2,
    minWidth: 200,
  },
  {
    field: 'licenseTypes',
    headerName: 'License Types',
    flex: 1,
    minWidth: 150,
    valueFormatter: (value: string[]) => value?.join(', ') || 'None',
  },
]

export default function UsersDataGrid({
  users,
  selectedUsers,
  onSelectionChange,
  searchQuery,
  licenseTypeFilter,
  availableLicenseTypes,
  onSearchChange,
  onLicenseTypeChange,
  onResetFilters,
  isLoading = false,
  field,
  editing = true,
  title,
}: UsersDataGridProps): ReactElement {
  let UsersToolbar = useCallback(
    () => <DataGridToolbar fileName="group-users" />,
    [],
  )

  // Calculate statistics for selected users
  let stats = useMemo(() => {
    let selectedUserRows =
      selectedUsers.type === 'exclude'
        ? users.filter((u) => !selectedUsers.ids.has(u.id))
        : users.filter((u) => selectedUsers.ids.has(u.id))

    return {
      userCount: selectedUserRows.length,
    }
  }, [users, selectedUsers])

  return (
    <Card className="p-6 mb-6">
      <Box className="flex flex-wrap items-center justify-between gap-2 mb-4">
        {title && (
          <Typography variant="h6" component="h2">
            {title}
          </Typography>
        )}

        <Box className="flex items-center gap-2">
          <Chip
            label={`${stats.userCount} user${stats.userCount !== 1 ? 's' : ''} selected`}
            size="medium"
            color="primary"
          />
          {(searchQuery || licenseTypeFilter) && (
            <Button
              className="ml-2 whitespace-nowrap"
              variant="outlined"
              color="secondary"
              onClick={onResetFilters}
              size="medium"
              aria-label="Reset filters"
              sx={{ height: 32 }}
            >
              Reset Filters
            </Button>
          )}
        </Box>
      </Box>
      {editing && (
        <UserFilters
          searchQuery={searchQuery}
          licenseTypeFilter={licenseTypeFilter}
          availableLicenseTypes={availableLicenseTypes}
          onSearchChange={onSearchChange}
          onLicenseTypeChange={onLicenseTypeChange}
        />
        // <Box className="grid grid-cols-1 gap-6 mb-4 md:grid-cols-2">
        //   <TextField
        //     fullWidth
        //     label="Search Users"
        //     placeholder="Search by name or email..."
        //     value={searchQuery}
        //     onChange={(e) => onSearchChange(e.target.value)}
        //     slotProps={{
        //       input: {
        //         startAdornment: <SearchIcon sx={{ mr: 1 }} />,
        //       },
        //     }}
        //     aria-label="Search users by name or email"
        //     sx={paperInputSx}
        //   />

        //   <FormControl fullWidth>
        //     <InputLabel id="license-type-filter-label">License Type</InputLabel>
        //     <Select
        //       labelId="license-type-filter-label"
        //       value={licenseTypeFilter}
        //       onChange={(e) => onLicenseTypeChange(e.target.value)}
        //       label="License Type"
        //       aria-label="Filter by license type"
        //       sx={{ backgroundColor: 'background.paper' }}
        //     >
        //       <MenuItem value="">
        //         <em>All License Types</em>
        //       </MenuItem>
        //       {availableLicenseTypes.map((licenseType) => (
        //         <MenuItem key={licenseType} value={licenseType}>
        //           {licenseType}
        //         </MenuItem>
        //       ))}
        //     </Select>
        //   </FormControl>
        // </Box>
      )}
      {shouldShowZodFieldError(field.state.meta) ? (
        <Alert severity="error" className="mb-4" role="alert">
          {getZodValidationError(field.state.meta.errors)}
        </Alert>
      ) : null}
      {isLoading ? (
        <Skeleton
          variant="rectangular"
          height={400}
          sx={{ borderRadius: 1 }}
          aria-label="Loading users"
        />
      ) : (
        <DataGridPremium
          rows={users}
          columns={userColumns}
          slots={{ toolbar: UsersToolbar }}
          showToolbar
          checkboxSelection={editing}
          disableRowSelectionOnClick
          rowSelectionModel={
            editing ? selectedUsers : { type: 'include', ids: new Set() }
          }
          onRowSelectionModelChange={onSelectionChange}
          initialState={{
            pagination: {
              paginationModel: { pageSize: DATAGRID_DEFAULT_PAGE_SIZE },
            },
          }}
          pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
          sx={dataGridSx}
          aria-label={editing ? 'Select users for group' : 'Group users'}
        />
      )}
    </Card>
  )
}
